import React from 'react';
import styles from './tv-guide.module.scss';
import clsx from 'clsx';
import testIds from '@smart-tv/e2e/shared/variables';
import {
  ArrowPressHandler,
  FocusContext,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import Button, { ButtonProps } from '@smart-tv/components/button/button';
import CalendarIcon from '@smart-tv/components/icons/calendar';
import FavoritesListIcon from '@smart-tv/components/icons/favorites-list';
import Typography from '@smart-tv/components/typography';
import ProgressBar from '@smart-tv/components/progress-bar/progress-bar';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';
import {
  DATE_FOCUS_KEY,
  FAVORITES_FOCUS_KEY,
  NOW_FOCUS_KEY
} from '@smart-tv/pages/tv-guide/utils/tv-guide-helpers';
import getDateButtonText from '@smart-tv/utils/date/get-date-button-text';
import getDateValue from '@smart-tv/utils/date/get-date-value';
import isBeforeDate from '@smart-tv/utils/date/is-before/is-before';
import formatDuration from '@smart-tv/utils/formatter/format-duration';
import formatTitle from '@smart-tv/utils/formatter/format-title';
import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import isYesterday from '@smart-tv/utils/date/is-yesterday';
import isTomorrow from '@smart-tv/utils/date/is-tomorrow';
import isToday from '@smart-tv/utils/date/is-today';

interface Props {
  onLiveClick: () => void;
  onArrowPress: ArrowPressHandler<ButtonProps>;
}

const TvGuideMoreInfo: React.FC<Props> = (props: Props) => {
  const { onLiveClick, onArrowPress } = props;

  const {
    selectedDate,
    setCalendarDialog,
    setFavoritesDialog,
    dateChanged,
    favoritesChanged,
    resetViewStates
  } = React.useContext(TvGuideContext);
  const { time, activeProgram } = React.useContext(TvGuideDayViewContext);

  const { ref, focusKey } = useFocusable({
    saveLastFocusedChild: false
  });

  const { selectedFavoritesList } = useFavoritesLists();

  const {
    startTime,
    endTime,
    startTimeInMinutes,
    durationInMinutes,
    title,
    genre,
    year,
    duration,
    period
  } = activeProgram;

  const periodText = () => {
    const programDate = new Date(startTime * 1000);
    const today = new Date();
    if (isToday(programDate)) {
      if (time.unix > endTime) {
        return `Sendt i dag kl. ${period}`;
      } else if (time.unix < startTime) {
        return `Sendes i dag kl. ${period}`;
      } else {
        return `Sendes nu kl. ${period}`;
      }
    }
    const yesterday = new Date(today.setDate(today.getDate() - 1));
    yesterday.setHours(23, 59, 59, 0);

    const lowerCaseDateValue = getDateValue(programDate).toLocaleLowerCase();

    if (isBeforeDate(programDate, yesterday)) {
      return isYesterday(programDate)
        ? `Sendt ${lowerCaseDateValue} kl. ${period}`
        : `Sendt i ${lowerCaseDateValue}s kl. ${period}`;
    } else {
      return isTomorrow(programDate)
        ? `Sendes ${lowerCaseDateValue} kl. ${period}`
        : `Sendes på ${lowerCaseDateValue} kl. ${period}`;
    }
  };

  const showProgressBar = () => {
    return startTime < time.unix && endTime > time.unix;
  };

  const getProgress = React.useCallback(() => {
    return ((time.minutes - startTimeInMinutes) / durationInMinutes) * 100;
  }, [time.minutes, startTimeInMinutes, durationInMinutes]);

  React.useEffect(() => {
    resetViewStates();
    return () => {
      resetViewStates();
    };
  }, [resetViewStates]);

  return (
    <div className={styles['tv-guide-more-info']}>
      <Typography
        className={styles['tv-guide-more-info__title']}
        noWrap
        variant="h3"
        data-testid={testIds.tvGuide.moreInfo.title}
      >
        {formatTitle(title)}
      </Typography>
      {(genre || year || duration) && (
        <Typography
          className={styles['tv-guide-more-info__text']}
          noWrap
          variant="h6"
          data-testid={testIds.tvGuide.moreInfo.info}
        >
          {genre}
          {genre && year && ' | '}
          {year}
          {(genre || year) && duration && ' | '}
          {formatDuration(duration)}
        </Typography>
      )}
      {period && (
        <Typography
          className={styles['tv-guide-more-info__period']}
          noWrap
          data-testid={testIds.tvGuide.moreInfo.period}
        >
          {periodText()}
        </Typography>
      )}    
      <div className={clsx(
          styles['tv-guide-more-info__progress'],
          showProgressBar() && styles['tv-guide-more-info__progress--visible']
        )}
      >
        <ProgressBar
          className={styles['tv-guide-more-info__progress-bar']}
          color="success"
          height={8}
          value={showProgressBar() ? getProgress() : 0}
          data-testid={testIds.tvGuide.moreInfo.progressBar}
        />
      </div>     
      <FocusContext.Provider value={focusKey}>
        <div className={styles['tv-guide-more-info__buttons']} ref={ref}>
          <Button
            className={styles['tv-guide-more-info__button']}
            size="sm"
            onClick={onLiveClick}
            focusKey={NOW_FOCUS_KEY}
            onArrowPress={onArrowPress}
            data-testid={testIds.tvGuide.moreInfo.buttons.live}
          >
            Lige nu
          </Button>
          <Button
            className={styles['tv-guide-more-info__button']}
            size="sm"
            onClick={() => {
              setCalendarDialog(true);
            }}
            focusKey={DATE_FOCUS_KEY}
            onArrowPress={onArrowPress}
            data-testid={testIds.tvGuide.moreInfo.buttons.date}
            selected={dateChanged}
          >
            <CalendarIcon className={styles['tv-guide-more-info__button-icon']} />
            {dateChanged ? getDateButtonText(selectedDate.date) : 'Vælg dato'}
          </Button>
          <Button
            className={styles['tv-guide-more-info__button']}
            size="sm"
            onClick={() => {
              setFavoritesDialog(true);
            }}
            focusKey={FAVORITES_FOCUS_KEY}
            onArrowPress={onArrowPress}
            data-testid={testIds.tvGuide.moreInfo.buttons.favorites}
            selected={favoritesChanged}
          >
            <FavoritesListIcon className={styles['tv-guide-more-info__button-icon']} />
            {favoritesChanged && selectedFavoritesList?.name
              ? selectedFavoritesList?.name
              : 'Vælg kanalliste'}
          </Button>
        </div>
      </FocusContext.Provider>
    </div>
  );
};

export default React.memo(TvGuideMoreInfo);
